import secureLocalStorage from 'react-secure-storage';
import AppConfig from '../lib/AppConfig';
import { AuthResponse } from '../modules/Auth/types/Auth';

export function setUserDetails(user: AuthResponse | null) {
	try {
		secureLocalStorage.setItem('user', JSON.stringify(user));
	} catch {
		// ignore storage write errors
	}
}

export function getUserDetails(): AuthResponse | null {
	try {
		const data = secureLocalStorage.getItem('user');
		return data ? (JSON.parse(data as string) as AuthResponse) : null;
	} catch {
		return null;
	}
}

export function setToken(token: string) {
	try {
		secureLocalStorage.setItem('token', JSON.stringify(token));
	} catch {}
}

export function set2faToken(token?: string) {
	try {
		secureLocalStorage.setItem('token2fa', JSON.stringify(token));
	} catch {}
}

export function remove2faToken() {
	try {
		secureLocalStorage.removeItem('token2fa');
	} catch {}
}

export function removeToken() {
	try {
		secureLocalStorage.removeItem('token');
	} catch {}
}

export async function getToken(): Promise<string> {
	try {
		const token = secureLocalStorage.getItem('token') as string;
		return Promise.resolve(token ? token.replaceAll('"', '') : '');
	} catch {
		return Promise.resolve('');
	}
}

// ✅ Always fulfills and never throws
export async function get2faToken(): Promise<string> {
	try {
		const token = secureLocalStorage.getItem('token2fa') as string;
		return Promise.resolve(token ? token.replaceAll('"', '') : '');
	} catch {
		return Promise.resolve('');
	}
}

// ✅ Always resolves, never rejects
export async function getAuthAPIHeaders(defaultHeaders?: Record<string, string>): Promise<Headers> {
	try {
		const headers = new Headers(defaultHeaders);
		const token = await getToken();
		headers.append('Authorization', `Basic ${token}`);
		headers.append('Fineract-Platform-Tenantid', AppConfig.tenantId);
		return Promise.resolve(headers);
	} catch {
		return Promise.resolve(new Headers(defaultHeaders));
	}
}

// ✅ Always resolves safely even if token retrieval fails
export async function getAPIHeaders(defaultHeaders?: Record<string, string>): Promise<Headers> {
	try {
		const headers = await getAuthAPIHeaders(defaultHeaders);
		const mfaToken = await get2faToken();

		if (mfaToken) {
			headers.append('fineract-platform-tfa-token', mfaToken);
		}

		return Promise.resolve(headers);
	} catch {
		return Promise.resolve(new Headers(defaultHeaders));
	}
}

// ---------- Utilities ----------
export function bytesToKilobytes(bytes: number): string {
	return (bytes / 1024).toFixed(2);
}

function jaccardSimilarity(arr1: string[], arr2: string[]): number {
	const set1 = new Set(arr1);
	const set2 = new Set(arr2);
	const intersection = new Set([...set1].filter(x => set2.has(x)));
	const union = new Set([...set1, ...set2]);
	return intersection.size / union.size;
}

function calculateNameSimilarity(name1: string, name2: string): number {
	const arr1 = name1
		.toLowerCase()
		.split(' ')
		.sort()
		.map(n => n.trim());
	const arr2 = name2
		.toLowerCase()
		.split(' ')
		.sort()
		.map(n => n.trim());
	return jaccardSimilarity(arr1, arr2) * 100;
}

export function areNamesSimilar(name1: string, name2: string): boolean {
	return calculateNameSimilarity(name1, name2) > 90;
}

export function generateTransactionReceipt(): string {
	const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
	let random = '';
	for (let i = 0; i < 10; i++) {
		random += chars[Math.floor(Math.random() * chars.length)];
	}
	return `KBS${random}`;
}

export function generateRandomString(length = 10): string {
	const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	let result = '';
	for (let i = 0; i < length; i++) {
		result += chars[Math.floor(Math.random() * chars.length)];
	}
	return result;
}

export function getNestedValue(obj: any, path: string): any {
	return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

export function fileToBase64(file: File): Promise<string> {
	return new Promise(resolve => {
		try {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = () => resolve('');
			reader.readAsDataURL(file);
		} catch {
			resolve('');
		}
	});
}

// ---------- Query Builder ----------
export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export function buildQuery(params?: QueryParams): string {
	const usp = new URLSearchParams();

	for (const [key, value] of Object.entries(params ?? {})) {
		if (value === undefined || value === null) continue;

		if (typeof value === 'string') {
			const trimmed = value.trim();
			if (trimmed !== '') usp.append(key, trimmed);
		} else if (typeof value === 'number') {
			usp.append(key, value.toString());
		} else if (typeof value === 'boolean') {
			usp.append(key, value ? 'true' : 'false');
		}
	}

	return usp.toString();
}
